import ChatBarTitle from "@/components/communications-chat-components/ChatBarTitle";
import SuggestedUsersTextField from "@/components/communications-chat-components/SuggestedUsersTextField";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { useControlNavbarColor } from "@/hooks/useControlNavbarColor";
import { useResponsive } from "@/hooks/useResponsive";
import i18n from "@/i18n/config";
import { LazoUser } from "@/models/LazoUser";
import { SmallUserDTO } from "@/models/SmallUserDTO";
import { ChatMessageFragmentType } from "@/models/ai-chat/ChatMessageFragment";
import { LazoChatConversation, LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatMessage } from "@/models/ai-chat/LazoChatMessage";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AvatarGroup, Box, IconButton, SxProps, avatarClasses, avatarGroupClasses, styled } from "@mui/material";
import Image from "next/image";
import React, { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { MentionItem } from "react-mentions";
import settings from "../../../assets/images/settings.svg";
import { ChatAvatar } from "./ChatAvatar";
import { useOneChatContext } from "./one/OneChatContext";

export type MentionsFn = (ev: { target: { value: string } }, mentions: MentionItem[], filteredUsers: SmallUserDTO[]) => void;

type ChatBarProps = {
  conversation: LazoChatConversationShell;
  setOpenSettings: Dispatch<React.SetStateAction<boolean>>;
  setNewConversationMembers: Dispatch<SetStateAction<string[]>>;
  onChatSelect: (conv: LazoChatConversationShell) => void;
  chats: LazoChatConversationShell[];
  setChats: Dispatch<SetStateAction<LazoChatConversationShell[]>>;
  chatPreview: LazoChatConversation | null;
  setChatPreview: Dispatch<SetStateAction<LazoChatConversation | null>>;
  members: SmallConversationMember[];
};

/**
 * This generates a beginning of conversation message on the front side, optimistically
 */
export function generateFirstChatMessage(users: SmallUserDTO[]): LazoChatMessage {
  let textMessage = "";
  const mappedUsers: LazoUser[] = users?.map(
    (u) =>
      ({
        user: {
          externalId: u.externalId,
          firstName: u.firstName,
          lastName: u.lastName,
        },
      }) as LazoUser
  );
  if (users.length === 1) {
    textMessage += i18n.t("chat:chatBar.conversationBeginning");
    const u: User = mappedUsers[0].user;
    textMessage += `@[${u.firstName} ${u.lastName}](${u.externalId})`;
  } else if (users.length < 5) {
    textMessage += i18n.t("chat:chatBar.conversationBeginning");
    mappedUsers.forEach(({ user }, idx) => {
      textMessage += `@[${user.firstName} ${user.lastName}](${user.externalId})`;
      if (idx === mappedUsers.length - 2) {
        textMessage += i18n.t("chat:chatBar.conversationBeginningAnd");
      }
    });
  }

  return {
    message: textMessage,
    messageFragments: [{ type: ChatMessageFragmentType.CHAT_BEGINNING, content: mappedUsers }],
  };
}

export function generateSingleFirstChatMessage(user: User): LazoChatMessage {
  let textMessage = "";
  textMessage += i18n.t("chat:chatBar.conversationBeginning");
  const u: User = user;
  textMessage += `@[${u.firstName} ${u.lastName}](${u.externalId})`;
  return {
    message: textMessage,
    messageFragments: [{ type: ChatMessageFragmentType.CHAT_BEGINNING, content: [{ user: user }] }],
  };
}

function ChatBar(props: ChatBarProps) {
  const { t } = useTranslation("chat");
  const currentUserExternalId = AuthService.getUser()?.externalId;
  const tenantMembership = useTenantMemberships().currentTenantMembership;
  const { showMobileChatView, setShowMobileChatView } = useOneChatContext();
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  useControlNavbarColor(Colors.WHITE);

  function handleBackToList() {
    setShowMobileChatView(false);
  }

  function handleMentionsChange(_event: { target: { value: string } }, mentions: MentionItem[], filteredUsers: SmallUserDTO[]) {
    const newConvMembers = mentions.reduce((filtered, item) => {
      if (!filtered.includes(item.id)) {
        filtered.push(item.id);
      }
      return filtered;
    }, []);
    props.setNewConversationMembers(newConvMembers);

    // Show preview while creating conversation
    generateConversationPreview(newConvMembers, filteredUsers);
  }

  function generateConversationPreview(newConvMembers: string[], filteredUsers: SmallUserDTO[]) {
    if (newConvMembers.length === 0) {
      props.setChatPreview(null);
      return;
    }

    const members = [...newConvMembers, currentUserExternalId];

    LazoChatConversationService.findConversationByMembers(members, tenantMembership.tenant.externalId).then((r) => {
      r.onSuccess((l) => {
        if (props.chatPreview && props.chatPreview.externalId === l.externalId) {
          return;
        }

        props.setChatPreview(l);
      });

      r.onError(() => {
        props.setChatPreview({
          messages: [generateFirstChatMessage(filteredUsers)],
        } as LazoChatConversation);
      });
    });
  }

  return (
    <>
      <Bar>
        <Container>
          <AvatarContainer>
            {showMobileChatView && isMobileOrTablet && (
              <IconButton
                onClick={handleBackToList}
                sx={{
                  padding: "8px",
                  color: Colors.ELEGANT_BLACK,
                  marginRight: "8px",
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <ChatAvatar conversation={props.conversation} />
            <ChatBarTitle onChatSelect={props.onChatSelect} conversation={props.conversation} setChats={props.setChats} />
          </AvatarContainer>

          <ActionsContainer>
            {props.conversation && (
              <GrayContainer onClick={() => props.setOpenSettings(true)}>
                <Image width={16} height={16} alt={t("chatBar.settingsButton")} src={settings.src} />
              </GrayContainer>
            )}
          </ActionsContainer>
        </Container>
      </Bar>
      {!props.conversation && (
        <SuggestedUsersTextField
          onMentionsChange={handleMentionsChange}
          conversationExternalId={null}
          previewMembers={props.chatPreview?.members || null}
        />
      )}
    </>
  );
}

const ActionsContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "1rem",
  position: "relative",
  right: "1rem",
}));

const Container = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  //margin: "1rem"
  padding: "1rem",
}));

const Bar = styled(Box)(() => ({
  width: "100%",
  position: "sticky",
  height: "3.9rem",
  display: "flex",
  alignItems: "center",
  top: 0,
  backgroundColor: Colors.WHITE,
  zIndex: 2,
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} `,
}));

const AvatarContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const UserAvatarSx: SxProps = {
  width: "20px",
  height: "20px",
  borderRadius: "0.125rem",
  backgroundColor: "#fff",
  backgroundImage: "none",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} `,
  color: Colors.ELEGANT_BLACK,
  fontWeight: 400,
  fontSize: "0.625rem",
};

const ChatBarUserAvatarSx: SxProps = {
  borderRadius: "0.5rem",
  backgroundColor: "#fff",
  backgroundImage: "none",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} `,
  color: Colors.ELEGANT_BLACK,
  fontSize: "1.125rem",
};

const GrayContainer = styled(Box)(() => ({
  height: "2rem",
  display: "flex",
  padding: "0.25rem 0.5rem",
  gap: "0.5rem",
  alignItems: "center",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} `,
  borderRadius: "0.25rem",
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  ":hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    cursor: "pointer",
  },
}));

const StyledAvatarGroup = styled(AvatarGroup)(() => ({
  display: "flex",
  alignItems: "center",
  [`& .${avatarClasses.root} `]: {
    padding: 0,
    width: "fit-content",
    border: 0,
    borderRadius: 0,
    height: "fit-content",
    backgroundColor: "transparent",
  },
  [`& .${avatarGroupClasses.avatar} `]: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: Colors.BLACK_151517,
    border: 0,
    backgroundColor: "transparent",
    height: "100%",
    width: "fit-content",
    margin: 0,
    padding: 0,
    marginLeft: "0.5rem",
  },
}));

export default ChatBar;
